import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CACHE_KEY = "sm_pricing_v1";
const CACHE_TTL = 5 * 60 * 1000; // 5 minutes

// Fallback used if both cache and API fail (same defaults as backend seeder).
const FALLBACK_ITEMS = [
  { slug: "holodomy",    title: "Holodomy",        rate: 960, unit: "€/m²", features: ["Zemné práce", "Základy", "Murivo", "Strecha", "Okná"],          featured: false, visible: true, order: 1 },
  { slug: "zateplenie",  title: "Zateplenie",      rate: 48,  unit: "€/m²", features: ["Polystyrén", "Lepidlá", "Omietka", "Lešenie", "Doprava"],        featured: true,  visible: true, order: 2 },
  { slug: "ploty",       title: "Betónové ploty",  rate: 56,  unit: "€/bm", features: ["Stĺpiky", "Panely", "Cement", "Montáž", "Záruka"],                featured: false, visible: true, order: 3 },
  { slug: "zaklady",     title: "Základy RD",      rate: 90,  unit: "€/m²", features: ["Výkopy", "Debnenie", "Armatúra", "Betón", "Izolácia"],           featured: false, visible: true, order: 4 },
  { slug: "dlazba",      title: "Zámková dlažba",  rate: 70,  unit: "€/m²", features: ["Podklad", "Štrk", "Dlažba", "Obrubníky", "Škárovanie"],          featured: false, visible: true, order: 5 },
  { slug: "sadrokarton", title: "Sadrokartón",     rate: 32,  unit: "€/m²", features: ["SDK dosky", "Konštrukcia", "Tmelenie", "Brúsenie", "S materiálom"], featured: false, visible: true, order: 6 },
];

function _readCache() {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.ts || Date.now() - parsed.ts > CACHE_TTL) return null;
    return parsed.items;
  } catch {
    return null;
  }
}

function _writeCache(items) {
  try {
    localStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), items }));
  } catch { /* ignore */ }
}

/**
 * React hook that returns current pricing list.
 * Returns { items, bySlug, loading } — items is always non-empty thanks to the
 * fallback, so callers never need loading guards for pricing to render.
 */
export function usePricing() {
  const [items, setItems] = useState(() => _readCache() || FALLBACK_ITEMS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    axios
      .get(`${API}/pricing`)
      .then(({ data }) => {
        if (!alive) return;
        const fresh = data?.items?.length ? data.items : FALLBACK_ITEMS;
        setItems(fresh);
        _writeCache(fresh);
      })
      .catch(() => {})
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, []);

  const bySlug = Object.fromEntries(items.map((i) => [i.slug, i]));
  return { items, bySlug, loading };
}

/** Format a price item into '48 €/m²' (no 'od ' prefix). */
export function formatPrice(item) {
  if (!item) return "";
  const r = Number(item.rate);
  const rate = Number.isInteger(r) ? r : r.toFixed(2);
  return `${rate} ${item.unit || "€/m²"}`;
}

/** '48€/m²' compact form used in marquee + cards. */
export function formatPriceCompact(item) {
  if (!item) return "";
  const r = Number(item.rate);
  const rate = Number.isInteger(r) ? r : r.toFixed(2);
  const unit = (item.unit || "€/m²").replace(/\s+/g, "");
  return `${rate}${unit}`;
}

export { FALLBACK_ITEMS as DEFAULT_PRICING };
